import React, { useCallback } from 'react';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import { Stack } from '@/components/layout';
import { Text, WebView } from '@/components/ui';
import { FLOATING_TAB_BAR_CLEARANCE_TOKEN } from '@/components/ui/tabbar';
import { STRINGS } from '@/constants/strings';
import { PRIVACY_POLICY_HTML } from '@/features/auth/components/legal';
import { buildLegalDocumentHtml } from '@/features/auth/components/legal/legal-document-html';
import { GuardianProfileActionHeader } from '@/features/guardian-profile/components';
import { createStyles, useGuardianStackNavigation, useScaling } from '@/hooks';
import { useLegalDocumentQuery } from '@/queries/use-legal-documents-query';
import { colors, guardianColors } from '@/theme/colors';

type GuardianPrivacyLegalContentProps = {
  tabletPanel?: boolean;
};

export function GuardianPrivacyLegalScreen() {
  const { isTablet } = useScaling();
  const insets = useSafeAreaInsets();
  const navigation = useGuardianStackNavigation();

  const goBack = useCallback(() => {
    if (navigation.canGoBack()) {
      navigation.goBack();
      return;
    }
    navigation.navigate('GuardianDashboard');
  }, [navigation]);

  return (
    <Stack flex={1} backgroundColor={colors['1']}>
      <GuardianProfileActionHeader
        title={STRINGS.guardianProfile.settings.legal}
        isTablet={isTablet}
        topInset={insets.top}
        onBack={goBack}
      />
      <GuardianPrivacyLegalContent />
    </Stack>
  );
}

export function GuardianPrivacyLegalContent({
  tabletPanel = false,
}: GuardianPrivacyLegalContentProps) {
  const { scale, isTablet } = useScaling();
  const insets = useSafeAreaInsets();
  const styles = useStyles();
  const { data: document } = useLegalDocumentQuery('privacy_policy');

  // The API returns an HTML fragment; the shared builder wraps it in the styled page the
  // WebView expects. Falls back to the bundled copy so a failed request still shows a policy.
  const html = document ? buildLegalDocumentHtml(document.content) : PRIVACY_POLICY_HTML;
  const bottomPadding = tabletPanel
    ? insets.bottom + scale(FLOATING_TAB_BAR_CLEARANCE_TOKEN)
    : insets.bottom + scale(16);

  return (
    <Stack
      flex={1}
      style={[
        styles.content,
        {
          paddingBottom: bottomPadding,
        },
      ]}>
      {tabletPanel ? (
        <Text variant="Font Guardian App/Heading/Heading 24" style={styles.panelTitle}>
          {STRINGS.guardianProfile.settings.legal}
        </Text>
      ) : null}
      <Stack style={styles.card}>
        <WebView
          source={{ html }}
          originWhitelist={['*']}
          style={styles.webview}
          containerStyle={styles.webview}
          showsVerticalScrollIndicator={false}
          forceMobileOnTablet={false}
          isTablet={isTablet}
          androidLayerType="hardware"
        />
      </Stack>
    </Stack>
  );
}

const useStyles = createStyles(({ scale }) => ({
  content: {
    alignItems: 'center',
    paddingTop: scale({ phone: 12, tablet: 16 }),
    paddingHorizontal: scale({ phone: 16, tablet: 20 }),
  },
  screenContent: {
    paddingHorizontal: scale({ phone: 16, tablet: 24 }),
    paddingTop: scale({ phone: 4, tablet: 16 }),
  },
  tabletPanelContent: {
    paddingTop: scale(25),
    paddingHorizontal: scale(20),
  },
  panelTitle: {
    alignSelf: 'stretch',
    color: guardianColors['10'],
    marginBottom: scale(20),
  },
  card: {
    flex: 1,
    width: '100%',
    borderRadius: scale(12),
    borderCurve: 'continuous',
    backgroundColor: colors['0'],
    shadowColor: '#222B45',
    shadowOffset: { width: 0, height: 0 },
    shadowOpacity: 0.04,
    shadowRadius: scale(8),
    elevation: 1,
    paddingHorizontal: scale({ phone: 12, tablet: 18 }),
    paddingVertical: scale({ phone: 10, tablet: 16 }),
  },
  tabletPanelCard: {
    marginTop: scale(26),
    paddingLeft: scale(18),
    paddingRight: scale(12),
    paddingVertical: scale(16),
  },
  webview: {
    flex: 1,
    backgroundColor: 'transparent',
  },
}));
